import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";

/**
 * `PromptInputTextarea` + `@`-mention autocomplete.
 *
 * Drop-in for `PromptInputTextarea`: while a `@<query>` token sits at the
 * caret, a floating panel anchored to the textarea shows up to 8 matching
 * workspace files. Up/Down navigates, Enter / Tab selects, Escape closes.
 *
 * Selecting a file:
 *   - removes the `@query` from the textarea text
 *   - calls `onMentionFile(path)` so the parent composer can add the path
 *     to its references list (rendered separately as a chip via
 *     `ContextChips`)
 *
 * No autocomplete UI is shown for paths already in `excludePaths` — keeps
 * the popover from suggesting things already attached.
 *
 * Why a portal: the ai-elements `PromptInput` wraps its body in
 * `InputGroup` with `overflow-hidden`, so a popover anchored *inside* the
 * group gets clipped at the edges of the textarea. And `InputGroup` uses
 * direct-child selectors (`has-[>textarea]`) for its layout — wrapping the
 * textarea in a `position: relative` div breaks both the height
 * calculation and the padding rules. The portal sidesteps both problems:
 * the textarea stays a direct child of `InputGroup`, and the popover
 * renders in `document.body` positioned by `getBoundingClientRect()`.
 */
import {
  PromptInputCommand,
  PromptInputCommandEmpty,
  PromptInputCommandGroup,
  PromptInputCommandItem,
  PromptInputCommandList,
  PromptInputTextarea,
  type PromptInputTextareaProps,
} from "../ai-elements/prompt-input";
import { cn } from "../lib/utils";
import {
  detectMentionTrigger,
  filterMentionSuggestions,
  type MentionTrigger,
} from "./file-mentions";

export interface FileMentionTextareaProps extends Omit<
  PromptInputTextareaProps,
  "ref"
> {
  /** Workspace paths available for autocomplete. */
  allPaths: string[];
  /** Paths to omit from the autocomplete list (already attached). */
  excludePaths?: string[];
  /** Called when the user accepts a suggestion. */
  onMentionFile: (path: string) => void;
  /** Show suggestions below a composer near the top of the viewport. */
  mentionPlacement?: "above" | "below";
}

export const FileMentionTextarea = forwardRef<
  HTMLTextAreaElement,
  FileMentionTextareaProps
>(function FileMentionTextarea(
  {
    allPaths,
    excludePaths,
    onMentionFile,
    mentionPlacement = "above",
    onChange,
    onKeyDown,
    className,
    ...rest
  },
  forwardedRef,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(forwardedRef, () => innerRef.current!, []);

  const [trigger, setTrigger] = useState<MentionTrigger | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const excludeSet = useMemo(() => new Set(excludePaths ?? []), [excludePaths]);

  const availablePaths = useMemo(
    () => allPaths.filter((p) => !excludeSet.has(p)),
    [allPaths, excludeSet],
  );

  const suggestions = useMemo(() => {
    if (!trigger) return [];
    return filterMentionSuggestions(availablePaths, trigger.query, 8);
  }, [trigger, availablePaths]);

  const refreshTrigger = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    const next = detectMentionTrigger(el.value, el.selectionStart ?? 0);
    setTrigger(next);
    setActiveIndex(0);
  }, []);

  const closeTrigger = useCallback(() => setTrigger(null), []);

  const acceptSuggestion = useCallback(
    (path: string) => {
      const el = innerRef.current;
      if (!el || !trigger) return;
      // Replace the `@query` with a wiki-link to the picked file. We
      // insert the path verbatim (including `.md`); the wiki-link
      // resolver handles both `[[Notes]]` and `[[Notes.md]]` so this is
      // unambiguous either way.
      const insertion = `[[${path}]] `;
      const before = el.value.slice(0, trigger.start);
      const after = el.value.slice(trigger.end);
      const next = before + insertion + after;
      // Drive the textarea through its native value setter so React's
      // synthetic onChange fires and the SDK's controller (if any) stays in
      // sync. Direct .value assignment skips React's event hook.
      const setter = Object.getOwnPropertyDescriptor(
        HTMLTextAreaElement.prototype,
        "value",
      )?.set;
      setter?.call(el, next);
      el.dispatchEvent(new Event("input", { bubbles: true }));
      el.focus();
      const caret = trigger.start + insertion.length;
      el.setSelectionRange(caret, caret);
      onMentionFile(path);
      setTrigger(null);
    },
    [trigger, onMentionFile],
  );

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    refreshTrigger();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (trigger) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeTrigger();
        return;
      }
      if (suggestions.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % suggestions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex(
            (i) => (i - 1 + suggestions.length) % suggestions.length,
          );
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          const pick = suggestions[activeIndex];
          if (pick) acceptSuggestion(pick);
          return;
        }
      }
    }
    onKeyDown?.(e);
  };

  // Caret can move without a value change (arrow keys, click). Re-evaluate
  // the trigger on those interactions so the popover opens/closes naturally.
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const onClickOrKey = () => refreshTrigger();
    const onBlur = () => window.setTimeout(() => setTrigger(null), 120);
    el.addEventListener("click", onClickOrKey);
    el.addEventListener("keyup", onClickOrKey);
    el.addEventListener("blur", onBlur);
    return () => {
      el.removeEventListener("click", onClickOrKey);
      el.removeEventListener("keyup", onClickOrKey);
      el.removeEventListener("blur", onBlur);
    };
  }, [refreshTrigger]);

  return (
    <>
      <PromptInputTextarea
        ref={innerRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={className}
        {...rest}
      />
      {trigger ? (
        <MentionPopover anchor={innerRef.current} placement={mentionPlacement}>
          <PromptInputCommand shouldFilter={false}>
            <PromptInputCommandList className="max-h-64">
              {suggestions.length === 0 ? (
                <PromptInputCommandEmpty className="px-3 py-4 text-center text-sm text-muted-foreground">
                  {availablePaths.length === 0
                    ? "No workspace files"
                    : "No matching files"}
                </PromptInputCommandEmpty>
              ) : (
                <PromptInputCommandGroup heading="Workspace files">
                  {suggestions.map((path, i) => (
                    <PromptInputCommandItem
                      key={path}
                      value={path}
                      onSelect={() => acceptSuggestion(path)}
                      onMouseDown={(e) => e.preventDefault()}
                      className={cn(
                        "cursor-pointer",
                        i === activeIndex && "bg-accent text-accent-foreground",
                      )}
                    >
                      {path}
                    </PromptInputCommandItem>
                  ))}
                </PromptInputCommandGroup>
              )}
            </PromptInputCommandList>
          </PromptInputCommand>
        </MentionPopover>
      ) : null}
    </>
  );
});

/**
 * Portal-anchored popover positioned just above the given textarea.
 * Tracks scroll/resize so the popover stays glued during scrolling. Width
 * matches the textarea so suggestions feel like an extension of the
 * input. 8px gap keeps it from kissing the border.
 */
function MentionPopover({
  anchor,
  placement,
  children,
}: {
  anchor: HTMLTextAreaElement | null;
  placement: "above" | "below";
  children: React.ReactNode;
}) {
  const [rect, setRect] = useState<DOMRect | null>(
    () => anchor?.getBoundingClientRect() ?? null,
  );

  useLayoutEffect(() => {
    if (!anchor) return;
    const update = () => setRect(anchor.getBoundingClientRect());
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchor]);

  if (!rect || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="z-50 overflow-hidden rounded-md border border-border bg-popover shadow-md"
      style={{
        position: "fixed",
        left: rect.left,
        ...(placement === "above"
          ? { bottom: window.innerHeight - rect.top + 8 }
          : { top: rect.bottom + 8 }),
        width: rect.width,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
